import React from "react";

import { Monitor, Smartphone } from "lucide-react";

import { TooltipProvider } from "@/app/admin/_common/ui/tooltip";
import { Separator } from "@/app/admin/_common/ui/separator";
import { cn } from "@/app/_utils/cn";
import { Reader } from "../../documents/reader/CuoEmailReader";

import EditorBlock from "../../documents/editor/EditorBlock";
import {
  setSelectedScreenSize,
  useDocument,
  useSelectedMainTab,
  useSelectedScreenSize,
} from "../../documents/editor/EditorContext";
import ToggleInspectorPanelButton from "../InspectorDrawer/ToggleInspectorPanelButton";

import DownloadJson from "./DownloadJson";
import HtmlPanel from "./HtmlPanel";
import ImportJson from "./ImportJson";
import JsonPanel from "./JsonPanel";
import MainTabsGroup from "./MainTabsGroup";
import ShareButton from "./ShareButton";
import ToolbarIconButton from "./ToolbarIconButton";

export default function TemplatePanel() {
  const document = useDocument();
  const selectedMainTab = useSelectedMainTab();
  const selectedScreenSize = useSelectedScreenSize();

  const isMobile = selectedScreenSize === "mobile";

  const renderMainPanel = () => {
    // The mobile frame is a fixed 370px canvas, so it keeps a literal width/shadow rather
    // than a Tailwind token — it's mimicking a device, not app chrome.
    const frame = (children: React.ReactNode) => (
      <div
        className={cn("h-full", isMobile && "mx-auto min-h-[800px] w-[370px] shadow-sm")}
      >
        {children}
      </div>
    );

    switch (selectedMainTab) {
      case "editor":
        return frame(<EditorBlock id="root" />);
      case "preview":
        return frame(<Reader document={document} rootBlockId="root" />);
      case "html":
        return <HtmlPanel />;
      case "json":
        return <JsonPanel />;
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-full flex-col overflow-hidden">
        <div className="flex h-[49px] min-h-[49px] flex-shrink-0 items-center justify-between gap-4 border-b border-border bg-card px-4">
          <MainTabsGroup />

          <div className="flex items-center gap-1">
            <DownloadJson />
            <ImportJson />

            {/* Screen-size choice reads as a selected-state highlight rather than a bordered
                segmented track — a track would sit taller than the 30px controls beside it. */}
            <ToolbarIconButton
              tooltip="Desktop view"
              icon={<Monitor className="size-4" />}
              active={!isMobile}
              onClick={() => setSelectedScreenSize("desktop")}
            />
            <ToolbarIconButton
              tooltip="Mobile view"
              icon={<Smartphone className="size-4" />}
              active={isMobile}
              onClick={() => setSelectedScreenSize("mobile")}
            />

            <ShareButton />
            <Separator orientation="vertical" className="mx-1 h-5" />
            <ToggleInspectorPanelButton />
          </div>
        </div>

        <div className="min-h-0 w-full flex-1 overflow-auto">{renderMainPanel()}</div>
      </div>
    </TooltipProvider>
  );
}
